import { isoDateTime, z } from "../zod.js";
import { localeKeys } from "../locales.js";

// Module 05 — Waste & recovery: the CATALOG contract DTOs (spec 05 §2.1, §2.2,
// §2.7, §2.16 + the §5 catalog/settings rows). Slice 05-A ships the head of the
// module: the three catalogs the floor's capture grid and module 04's
// `operation_qty_reports.scrap_reason_code` FK need, plus the per-tenant
// settings singleton §3.3/§3.5 read unconditionally.
//
// Conventions carried from inventory/schemas.ts:
//  * every quantity/percentage travels as a DECIMAL STRING mirroring the
//    column's numeric(p,s) — the exact text Postgres returns, never a JSON
//    number (D-041). A tolerance compared as a float is precisely what the
//    exact-arithmetic doctrine keeps out of a costing decision.
//  * uuid ids; `{en, ru, zh, fr}` display names as a partial locale record with
//    `en` REQUIRED (see WasteLocaleName below).
//  * lists answer `{items: [...]}` — never a bare array (the module-01 defect
//    catalogue-list-envelopes.int.test.ts was built for).
//
// PAGINATION, DELIBERATELY ABSENT. §5's cursor convention exists for unbounded
// collections (the ledger, remnants, matches). These three are BOUNDED catalogs
// — the cable pack ships 9 categories, 14 causes and 2 price classes, and the
// floor PWA has to hold all of them offline to render a 3-tap grid. A paged
// catalog would mean a floor device that has "some of the categories". Spec 05
// §5's catalog rows are amended to say so.

// --- shared scalars -------------------------------------------------------

const Uuid = z.guid();

/**
 * A display name in the platform locale set (spec 05 §2.1 `name_i18n`).
 *
 * `en` is REQUIRED and every other locale is optional, matching the DB CHECK.
 * The alternative — demanding all four — reads well in a spec and produces
 * FAKE TRANSLATIONS in practice: a group_admin adding "Cable dust" at 21:00
 * would type the English string into the ru/zh/fr boxes to get past the form,
 * and the product would then be unable to tell a real translation from a
 * placeholder ever again. Missing locales fall back to `en` at render time,
 * visibly marked in S11.
 */
export const WasteLocaleName = z
  .object({
    ...localeKeys(z.string().min(1).max(120).optional()),
    en: z.string().min(1).max(120),
  })
  .openapi({ description: "Display name per locale; `en` required, others optional" });

/** `UPPER_SNAKE` machine code — stable, referenced by module 04's scrap FK. */
export const WasteCode = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{1,47}$/)
  .openapi({ example: "COPPER_OFFCUT", description: "Stable UPPER_SNAKE machine code" });

/** A `numeric(14,3)` length in metres as exact decimal text (D-041). */
export const WasteLengthM = z
  .string()
  .regex(/^\d{1,11}(\.\d{1,3})?$/)
  .openapi({ example: "500.000", description: "Exact decimal, ≤ 3 dp (numeric(14,3))" });

/** A `numeric(6,3)` percentage 0–100 as exact decimal text (D-041). */
export const WastePct = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,3})?$/)
  .refine((v) => Number(v) <= 100, { message: "must be ≤ 100" })
  .openapi({ example: "15.000", description: "Exact decimal percentage 0–100, ≤ 3 dp" });

/** A `numeric(8,3)` millimetre band as exact decimal text (D-041). */
export const WasteMm = z
  .string()
  .regex(/^\d{1,5}(\.\d{1,3})?$/)
  .openapi({ example: "0.050", description: "Exact decimal, ≤ 3 dp (numeric(8,3))" });

/** Where a catalog row came from (spec 05 §2.1; D-062 guard 1).
 *  - `vertical_pack` — the installed pack's row; converges on re-install and
 *    renders LOCKED in S11 until the operator presses "Customize".
 *  - `system` — provisioned for every tenant regardless of pack (DEFECT_SCRAP,
 *    HANDLING_DAMAGE, COUNT_LOSS); also locked, also customizable.
 *  - `tenant` — the customer's own row (or a pack/system row they edited: the
 *    edit DEMOTES the marker in the same statement that writes it). */
export const WasteCatalogSource = z.enum(["vertical_pack", "system", "tenant"]);

export const WasteDisposition = z.enum(["reuse", "regrind", "sell", "dispose", "pending"]);
export const RemnantFloorPricePolicy = z.enum(["above_cost", "none"]);

// --- §2.1 waste_categories ------------------------------------------------

export const WasteCategory = z
  .object({
    id: Uuid,
    code: WasteCode,
    name_i18n: WasteLocaleName,
    default_disposition: WasteDisposition,
    recoverable: z.boolean(),
    requires_dimensions: z.boolean(),
    requires_cause: z.boolean(),
    sort_order: z.number().int(),
    is_active: z.boolean(),
    source: WasteCatalogSource,
    /** True while the row is still the pack's/system's — the S11 lock signal.
     * Derived from `source`, sent explicitly so a client never re-implements
     * the rule (and so the rule can change without a client release). */
    locked: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .openapi("WasteCategory");

export const WasteCategoryCreateRequest = z
  .object({
    code: WasteCode,
    name_i18n: WasteLocaleName,
    default_disposition: WasteDisposition.optional(),
    recoverable: z.boolean().optional(),
    requires_dimensions: z.boolean().optional(),
    requires_cause: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(32767).optional(),
  })
  .openapi("WasteCategoryCreateRequest");

/** Everything a category edit may touch (spec 05 §5 row 2). `code` is absent
 * BY CONSTRUCTION: it is the stable machine key module 04's scrap FK and the
 * pack's convergence both address rows by; §5's "409 on code change" is
 * expressed here as "the field does not exist", which no client can get wrong.
 */
export const WasteCategoryUpdateRequest = z
  .object({
    name_i18n: WasteLocaleName.optional(),
    default_disposition: WasteDisposition.optional(),
    recoverable: z.boolean().optional(),
    requires_dimensions: z.boolean().optional(),
    requires_cause: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(32767).optional(),
    is_active: z.boolean().optional(),
  })
  .openapi("WasteCategoryUpdateRequest");

// --- §2.2 waste_cause_codes -----------------------------------------------

export const WasteCauseCode = z
  .object({
    id: Uuid,
    code: WasteCode,
    name_i18n: WasteLocaleName,
    /** Empty = valid for every category; else the picker is restricted. */
    category_ids: z.array(Uuid),
    blames_machine: z.boolean(),
    blames_supplier: z.boolean(),
    sort_order: z.number().int(),
    is_active: z.boolean(),
    source: WasteCatalogSource,
    locked: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .openapi("WasteCauseCode");

export const WasteCauseCreateRequest = z
  .object({
    code: WasteCode,
    name_i18n: WasteLocaleName,
    category_ids: z.array(Uuid).optional(),
    blames_machine: z.boolean().optional(),
    blames_supplier: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(32767).optional(),
  })
  .openapi("WasteCauseCreateRequest");

export const WasteCauseUpdateRequest = z
  .object({
    name_i18n: WasteLocaleName.optional(),
    category_ids: z.array(Uuid).optional(),
    blames_machine: z.boolean().optional(),
    blames_supplier: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(32767).optional(),
    is_active: z.boolean().optional(),
  })
  .openapi("WasteCauseUpdateRequest");

// --- §2.7 remnant_price_classes -------------------------------------------

export const RemnantPriceClass = z
  .object({
    id: Uuid,
    code: WasteCode,
    name_i18n: WasteLocaleName,
    discount_pct: WastePct,
    floor_price_policy: RemnantFloorPricePolicy,
    /** Auto-classing band `[min, max)` in metres; null = unbounded that side. */
    auto_assign_min_length_m: WasteLengthM.nullable(),
    auto_assign_max_length_m: WasteLengthM.nullable(),
    is_active: z.boolean(),
    source: WasteCatalogSource,
    locked: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .openapi("RemnantPriceClass");

export const RemnantPriceClassCreateRequest = z
  .object({
    code: WasteCode,
    name_i18n: WasteLocaleName,
    discount_pct: WastePct,
    floor_price_policy: RemnantFloorPricePolicy.optional(),
    auto_assign_min_length_m: WasteLengthM.nullable().optional(),
    auto_assign_max_length_m: WasteLengthM.nullable().optional(),
  })
  .openapi("RemnantPriceClassCreateRequest");

export const RemnantPriceClassUpdateRequest = z
  .object({
    name_i18n: WasteLocaleName.optional(),
    discount_pct: WastePct.optional(),
    floor_price_policy: RemnantFloorPricePolicy.optional(),
    auto_assign_min_length_m: WasteLengthM.nullable().optional(),
    auto_assign_max_length_m: WasteLengthM.nullable().optional(),
    is_active: z.boolean().optional(),
  })
  .openapi("RemnantPriceClassUpdateRequest");

// --- §2.16 waste_settings --------------------------------------------------

export const WasteSettingsResponse = z
  .object({
    exact_tolerance_m: WasteLengthM,
    max_overage_pct: WastePct,
    reservation_ttl_hours: z.number().int(),
    variance_tolerance_default_pct: WastePct,
    wire_diameter_tolerance_mm: WasteMm,
    /** Pack-seeded (§2.16 amendment): below this length a cut leaves no
     * remnant record — the piece is waste, not inventory. */
    min_remnant_track_length_m: WasteLengthM,
    /** Pack-seeded (§2.16 amendment): the tenant fallback recovery target. */
    recovery_target_pct_default: WastePct,
    /**
     * READ-ONLY (D-069). True once the tenant has moved either pack-seeded knob
     * (`min_remnant_track_length_m`, `recovery_target_pct_default`) — and it
     * never returns to false, because there is no un-customize.
     *
     * It is on the wire so S11 can say the consequence out loud BEFORE the
     * tenant acts: from here on a pack re-install stops correcting these two
     * values. §5 row 57 used to describe this response as "the settings row",
     * and a phrase is how a column disappears between two sections of one spec.
     * Not accepted on PUT — the flag is a CONSEQUENCE of an edit, never an
     * input to one; letting a client set it would let a tenant opt out of pack
     * corrections without changing a single value.
     */
    pack_defaults_customized: z.boolean(),
    updated_at: z.string(),
  })
  .openapi("WasteSettingsResponse");

export const WasteSettingsUpdateRequest = z
  .object({
    exact_tolerance_m: WasteLengthM.optional(),
    max_overage_pct: WastePct.optional(),
    reservation_ttl_hours: z.number().int().min(1).max(8760).optional(),
    variance_tolerance_default_pct: WastePct.optional(),
    wire_diameter_tolerance_mm: WasteMm.optional(),
    min_remnant_track_length_m: WasteLengthM.optional(),
    recovery_target_pct_default: WastePct.optional(),
  })
  .openapi("WasteSettingsUpdateRequest");

// --- list envelopes --------------------------------------------------------

export const WasteCategoryListResponse = z
  .object({
    items: z.array(WasteCategory),
    /** Present only when the caller asked `?include=causes` (spec 05 §5 row 1).
     * Absent — not empty — when they did not, so a client can tell "no causes"
     * from "did not ask". */
    causes: z.array(WasteCauseCode).optional(),
  })
  .openapi("WasteCategoryListResponse");

export const RemnantPriceClassListResponse = z
  .object({ items: z.array(RemnantPriceClass) })
  .openapi("RemnantPriceClassListResponse");

// ===========================================================================
// SLICE 05-B — the ledger, remnant registry and rollups (spec 05 §2.3, §2.6,
// §2.15; §5 events/remnants/rollups rows). Conventions as above: exact decimal
// text for quantities (D-041), integer minor units for money, and §7's
// field-level redactions expressed as OPTIONAL money keys — a redacted field
// is ABSENT, never null or zero, so a client cannot cache a misleading value.
// ===========================================================================

/** §2.3 `source` — which of the §3.1 six doors created the row. */
export const WasteEventSource = z.enum([
  "floor_report",
  "qc_disposition",
  "calibration_variance",
  "cut_offcut",
  "count_variance",
  "byproduct_output",
  "regrind_loss",
  "manual",
]);

/** The two doors the HTTP surface accepts (spec 05 §3.1.1 / §3.1.6). The other
 * four are consumer/in-txn doors — a client claiming `calibration_variance`
 * would be fabricating a machine's testimony. */
export const WasteEventApiSource = z.enum(["floor_report", "manual"]);

export const WasteEventDisposition = z.enum(["pending", "reuse", "regrind", "sell", "dispose"]);
export const WasteCostSource = z.enum(["lot_actual", "moving_average", "standard", "manual"]);
export const RemnantKind = z.enum(["wire_remnant", "cable_offcut", "sheet_offcut"]);
export const RemnantStatus = z.enum(["available", "reserved", "consumed", "sold", "scrapped"]);
export const RemnantReservedForType = z.enum([
  "sales_order_line",
  "wo_material_requirement",
  "remnant_match",
]);

/** A `numeric(14,3)` quantity as exact decimal text (D-041). */
export const WasteQty = z
  .string()
  .regex(/^\d{1,11}(\.\d{1,3})?$/)
  .refine((v) => Number(v) > 0, { message: "must be > 0" })
  .openapi({ example: "390.000", description: "Exact decimal quantity > 0, ≤ 3 dp" });

const MinorMoney = z.number().int();
const Cursor = z.string().nullable();

/** §2.3 — one ledger row. Money keys (`unit_cost_minor`, `cost_minor`,
 * `recovery_value_minor`, `recovery_credited_minor`) are OPTIONAL: absent for
 * roles outside the §7 cost set (group_admin, facility_admin, finance). */
export const WasteEvent = z
  .object({
    id: Uuid,
    event_no: z.string(),
    occurred_at: z.string(),
    facility_id: Uuid,
    department_id: Uuid.nullable(),
    category_id: Uuid,
    cause_code_id: Uuid.nullable(),
    source: WasteEventSource,
    work_order_id: Uuid.nullable(),
    wo_operation_id: Uuid.nullable(),
    /** The operative machine grain in P2 (D-101); `machine_id` fills additively
     * when module 08 lands. */
    work_center_id: Uuid.nullable(),
    machine_id: Uuid.nullable(),
    /** NULL for `source='manual'` unless explicitly attributed (D-102). */
    shift_instance_id: Uuid.nullable(),
    originating_shift_instance_id: Uuid.nullable(),
    reported_by: Uuid,
    item_type: z.enum(["material", "product"]),
    item_id: Uuid,
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid.nullable(),
    qty: WasteQty,
    uom: z.string(),
    unit_cost_minor: MinorMoney.optional(),
    cost_minor: MinorMoney.optional(),
    currency: z.string().length(3),
    cost_source: WasteCostSource,
    disposition: WasteEventDisposition,
    disposition_at: z.string().nullable(),
    disposition_by: Uuid.nullable(),
    disposition_ref_type: z
      .enum(["remnant", "pvc_batch", "waste_sale", "disposal_ticket"])
      .nullable(),
    disposition_ref_id: Uuid.nullable(),
    recovery_value_minor: MinorMoney.optional(),
    reverses_event_id: Uuid.nullable(),
    /** Set when a reversal row exists for THIS row (the pair nets to zero). */
    reversed_by_event_id: Uuid.nullable(),
    /**
     * The §2.17 correction pair's OTHER half (D-225 ruling 2). A `restatement`
     * is the re-recorded corrected quantity that stands beside a reversal; an
     * `addition` is the extra row a positive delta books on top of a report
     * that was understated. `corrects_event_no` is denormalised so a client can
     * NAME the corrected row without a second fetch — it composes the sentence
     * itself, in the reader's language, because the server no longer writes one.
     */
    corrects_event_id: Uuid.nullable(),
    corrects_event_no: z.string().nullable(),
    correction_kind: z.enum(["restatement", "addition"]).nullable(),
    photo_file_ids: z.array(Uuid),
    notes: z.string().nullable(),
    client_event_id: Uuid,
    finance_posting_id: Uuid.nullable(),
    recovery_credited_minor: MinorMoney.optional(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .openapi("WasteEvent");

/** §2.6 — a registry row. No money (§7: qc/storekeeper/floor see quantities
 * and dispositions only; the registry carries none). */
export const Remnant = z
  .object({
    id: Uuid,
    remnant_no: z.string(),
    facility_id: Uuid,
    kind: RemnantKind,
    item_type: z.enum(["material", "product"]),
    item_id: Uuid,
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid,
    length_m: WasteLengthM.nullable(),
    diameter_mm: WasteMm.nullable(),
    dims: z.record(z.string(), z.unknown()).nullable(),
    location_id: Uuid.nullable(),
    price_class_id: Uuid.nullable(),
    status: RemnantStatus,
    reserved_for_type: RemnantReservedForType.nullable(),
    reserved_for_id: Uuid.nullable(),
    reserved_at: z.string().nullable(),
    reserved_by: Uuid.nullable(),
    reservation_expires_at: z.string().nullable(),
    source_waste_event_id: Uuid.nullable(),
    registered_by: Uuid,
    consumed_ref_type: z.enum(["work_order", "sales_shipment"]).nullable(),
    consumed_ref_id: Uuid.nullable(),
    closed_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .openapi("Remnant");

/** §3.1.1's remnant leg of a capture: present when the category
 * `requires_dimensions` — the event and the registry row are born in ONE
 * transaction. */
export const WasteEventRemnantInput = z
  .object({
    kind: RemnantKind.optional(),
    length_m: WasteLengthM,
    diameter_mm: WasteMm.optional(),
    location_id: Uuid,
  })
  .openapi("WasteEventRemnantInput");

export const WasteEventCreateRequest = z
  .object({
    /** The device's replay key, a v4 or v7 UUID. Version 8 is reserved for
     * the keys the server derives itself and is refused with 409
     * IDEMPOTENCY_KEY_REUSE (D-303). */
    client_event_id: Uuid,
    occurred_at: isoDateTime({ offset: true }),
    facility_id: Uuid,
    category_id: Uuid,
    cause_code_id: Uuid.optional(),
    source: WasteEventApiSource,
    item_type: z.enum(["material", "product"]),
    item_id: Uuid,
    qty: WasteQty,
    uom: z.string().min(1).max(16),
    lot_id: Uuid.optional(),
    stock_unit_id: Uuid.optional(),
    work_order_id: Uuid.optional(),
    wo_operation_id: Uuid.optional(),
    work_center_id: Uuid.optional(),
    department_id: Uuid.optional(),
    /** MANUAL source only, and audit-logged (D-102): an explicit attribution by
     * a caller who may attribute. A floor_report's shift is always resolved
     * server-side from `occurred_at` and this field is refused. */
    shift_instance_id: Uuid.optional(),
    /** → `files(id)`, purpose `waste_evidence` — NEVER raw S3 keys (D-064 pt 8). */
    photo_file_ids: z.array(Uuid).max(6).optional(),
    /** §3.2 rung 4 — privileged (waste.cost.manual), audit-logged. */
    unit_cost_minor: MinorMoney.min(0).optional(),
    notes: z.string().max(2000).optional(),
    remnant: WasteEventRemnantInput.optional(),
  })
  .openapi("WasteEventCreateRequest");

export const WasteEventCreateResponse = z
  .object({
    event: WasteEvent,
    remnant: Remnant.optional(),
    /** §9.1 — true when `client_event_id` had already been recorded; `event`
     * is the ORIGINAL row, served only to the user who recorded it (D-303). */
    idempotent_replay: z.boolean(),
  })
  .openapi("WasteEventCreateResponse");

export const WasteEventListResponse = z
  .object({ items: z.array(WasteEvent), next_cursor: Cursor })
  .openapi("WasteEventListResponse");

export const WasteEventDispositionRequest = z
  .object({
    disposition: z.enum(["reuse", "regrind", "sell", "dispose"]),
    disposition_ref_type: z
      .enum(["remnant", "pvc_batch", "waste_sale", "disposal_ticket"])
      .optional(),
    disposition_ref_id: Uuid.optional(),
    recovery_value_minor: MinorMoney.min(0).optional(),
  })
  .openapi("WasteEventDispositionRequest");

export const WasteEventReverseRequest = z
  .object({ reason: z.string().min(1).max(500) })
  .openapi("WasteEventReverseRequest");

export const WasteEventReverseResponse = z
  .object({ original: WasteEvent, reversal: WasteEvent })
  .openapi("WasteEventReverseResponse");

// --- remnants (§5 rows) -----------------------------------------------------

export const RemnantRegisterRequest = z
  .object({
    /** Device idempotency key — the §9.1 dedupe for the S9 offline path. A
     * replay answers only the user who registered the remnant (D-303). */
    client_event_id: Uuid,
    kind: RemnantKind,
    item_type: z.enum(["material", "product"]),
    item_id: Uuid,
    length_m: WasteLengthM,
    diameter_mm: WasteMm.optional(),
    lot_id: Uuid.optional(),
    location_id: Uuid,
    /** An EXISTING module-06 unit (S9 scan path). Absent → the service creates
     * the unit through module-06-owned registration in the same transaction. */
    stock_unit_id: Uuid.optional(),
    source_waste_event_id: Uuid.optional(),
    notes: z.string().max(2000).optional(),
  })
  .openapi("RemnantRegisterRequest");

export const RemnantRegisterResponse = z
  .object({ remnant: Remnant, idempotent_replay: z.boolean() })
  .openapi("RemnantRegisterResponse");

export const RemnantListResponse = z
  .object({ items: z.array(Remnant), next_cursor: Cursor })
  .openapi("RemnantListResponse");

export const RemnantReserveRequest = z
  .object({
    reserved_for_type: RemnantReservedForType,
    reserved_for_id: Uuid,
    expires_at: isoDateTime({ offset: true }).optional(),
  })
  .openapi("RemnantReserveRequest");

export const RemnantReclassRequest = z
  .object({ price_class_id: Uuid, reason: z.string().max(500).optional() })
  .openapi("RemnantReclassRequest");

export const RemnantScrapRequest = z
  .object({ reason: z.string().min(1).max(500) })
  .openapi("RemnantScrapRequest");

// --- rollups (§2.15 / §5 `GET /rollups`) ------------------------------------

/** D-101: `machine` became `work_center`; a machine grouping is ADDED, never
 * substituted, when module 08 lands.
 *
 * `cause` is the SCRAP PARETO's grouping (§4 S13, added 2026-08-20). Every
 * other member reads the §2.15 projection, whose bucket key has no cause
 * column; this one regroups the LEDGER through the rebuild's own live-row and
 * facility-timezone expressions, which is why the two reconcile exactly rather
 * than approximately (§2.15's derivability law). It answers the question the
 * product could not answer at all before it existed — *why did we scrap, ranked
 * by what it cost*. */
export const WasteRollupGroupBy = z.enum([
  "facility",
  "category",
  "cause",
  "work_center",
  "shift",
  "day",
]);

/** One aggregate bucket. Buckets are per-UoM and per-currency (§9.14):
 * `total_qty` NEVER sums across UoMs and money never sums across currencies.
 * Money keys are absent for roles outside the §7 cost set. */
export const WasteRollupRow = z
  .object({
    facility_id: Uuid,
    rollup_date: z.string().optional(),
    shift_instance_id: Uuid.nullable().optional(),
    work_center_id: Uuid.nullable().optional(),
    category_id: Uuid.optional(),
    /** `group_by=cause` only. NULLABLE and PRESENT for the unattributed bucket:
     * a category whose `requires_cause` is false produces rows with no cause,
     * and dropping them would silently shrink the Pareto's denominator — the
     * share percentages would then be computed against a total that excludes
     * the very scrap nobody has explained. Absent (not null) for every other
     * grouping, so a client can tell "not a dimension of this query" from "this
     * bucket has no cause". */
    cause_code_id: Uuid.nullable().optional(),
    uom: z.string(),
    currency: z.string().length(3),
    event_count: z.number().int(),
    total_qty: z.string(),
    total_cost_minor: MinorMoney.optional(),
    recovered_value_minor: MinorMoney.optional(),
    handed_off_cost_minor: MinorMoney.optional(),
  })
  .openapi("WasteRollupRow");

export const WasteRollupResponse = z
  .object({ items: z.array(WasteRollupRow) })
  .openapi("WasteRollupResponse");

// --- problem codes (spec 05 §5/§9) ----------------------------------------

/**
 * The RFC 9457 `code`s this module's endpoints can raise. Gated both ways by
 * `test/unit/module-catalog-parity.test.ts`: a code raised in a handler that is
 * not here fails, and a code here that no handler can raise fails.
 */
export const wasteProblemCodes = [
  "WASTE_CATEGORY_NOT_FOUND",
  "WASTE_CAUSE_NOT_FOUND",
  "REMNANT_PRICE_CLASS_NOT_FOUND",
  "WASTE_CATEGORY_CODE_TAKEN",
  "WASTE_CAUSE_CODE_TAKEN",
  "REMNANT_PRICE_CLASS_CODE_TAKEN",
  "PRICE_CLASS_BAND_OVERLAP",
  "WASTE_CAUSE_CATEGORY_UNKNOWN",
  // 05-B — the ledger + remnants.
  "WASTE_EVENT_NOT_FOUND",
  "WASTE_CAUSE_REQUIRED",
  "WASTE_DIMENSIONS_REQUIRED",
  "WASTE_ITEM_UNKNOWN",
  "WASTE_EVIDENCE_INVALID",
  "WASTE_SHIFT_ATTRIBUTION_FORBIDDEN",
  "UOM_NOT_CONVERTIBLE",
  "DISPOSITION_FINAL",
  "WASTE_EVENT_ALREADY_REVERSED",
  "WASTE_EVENT_POSTED",
  "WASTE_REMNANT_NOT_FOUND",
  "REMNANT_NOT_AVAILABLE",
  "REMNANT_NOT_RESERVED",
  "REMNANT_BELOW_TRACK_LENGTH",
] as const;

export type WasteProblemCode = (typeof wasteProblemCodes)[number];
